import { FC, ReactElement, useMemo } from 'react';

import { getSourcesDisableReason, sourcesConfigToTree } from 'utils/campaign';
import { CampaignSubSourceDisableReason } from 'utils/constants/campaign';

import useIsEditAdvertising from 'utils/campaign/hooks/useIsEditAdvertising';

import withErrorBoundary from 'components/common/ErrorBoundary/withErrorBoundary';
import { LowBalanceCard } from 'components/CpcFlexibleCreate/SourcesAndBudgetsSidebar/SourceDisableCard/LowBalanceCard';
import { LowImgResCard } from 'components/CpcFlexibleCreate/SourcesAndBudgetsSidebar/SourceDisableCard/LowImgResCard';
import { NewSourcesNotAllowedCard } from 'components/CpcFlexibleCreate/SourcesAndBudgetsSidebar/SourceDisableCard/NewSourcesNotAllowedCard';

import { Source } from 'store/models/createCampaign';
import { useSelector } from 'store/utils';

const DISABLED_REASONS: Record<CampaignSubSourceDisableReason, ReactElement | null> = {
    [CampaignSubSourceDisableReason.LowBalance]: <LowBalanceCard />,
    [CampaignSubSourceDisableReason.LowImgRes]: <LowImgResCard />,
    [CampaignSubSourceDisableReason.LowImgResAndLowBalance]: null,
};

const SourceDisableCardComponent: FC = () => {
    const adSourcesConfig = useSelector((state) => state.createCampaign.adSourcesConfig);
    const savedSources = useSelector((state) => state.createCampaign.savedSources);
    const adSource = useSelector((state) => state.createCampaign.adSource);

    const isEditAdvertising = useIsEditAdvertising();

    const sourcesTree = useMemo(() => {
        return sourcesConfigToTree(adSourcesConfig);
    }, [adSourcesConfig]);

    const hasDisabledSourcesForEditAdvertising = useMemo((): boolean => {
        if (!isEditAdvertising) return false;
        const enabledSources: Source[] = sourcesTree.filter((source) => {
            const someSubSourcesSelected = source.subSources.some(({ subSource }) => adSource.includes(subSource));
            const someSubSourceSaved = source.subSources.some(({ subSource }) => savedSources.includes(subSource));
            return someSubSourcesSelected || someSubSourceSaved;
        });
        return enabledSources.length !== sourcesTree.length;
    }, [isEditAdvertising, sourcesTree, adSource, savedSources]);

    const disableReason = useMemo(() => {
        return getSourcesDisableReason(adSourcesConfig);
    }, [adSourcesConfig]);

    if (isEditAdvertising && hasDisabledSourcesForEditAdvertising) {
        return <NewSourcesNotAllowedCard />;
    }

    return disableReason ? DISABLED_REASONS[disableReason] : null;
};

const SourceDisableCard = withErrorBoundary(SourceDisableCardComponent);
export { SourceDisableCard };
